import React, {Component} from 'react';
import {observer} from 'mobx-react';
import {Flex, PriorityIcon, Button, Icon, Dropdown} from '@priority-software/priority-style-react';
import {SearchDropdown} from '../../FormHeader/components/Search/SearchDropdown';
import List from '../../../components/Common/List';
import {renderGeneratorNewState} from '../../Searches/renderGenerator';
import {QuickSearchItem} from './QuickSearchItem';
import {setIsReactPopupOpen} from '../../../_shared/GWTBridge/invoke/netForm';
import {returnFocus} from '../../../_shared/GWTBridge/invoke/genPage';
import {QuickSearchStore} from '../store/QuickSearchStore';
import {quickSearchEntStart} from '../../../_shared/GWTBridge/invoke/netForm';
import {keyCodes} from '../../../constants';
import {showHelpMessage} from '../../../_shared/Messages/actions/MessageActions';
import {sendMonitor} from '../../../utils/monitoring';
import {ButtonWithToolTip} from './ButtonWithTooltip';
import {ResultsStatus} from './ResultsStatus';
import texts from '../text';
import styles from '../styles/QuickSearch.module.scss';

interface QuickSearchProps {
    quickSearchStore: QuickSearchStore;
}

/**
 * @param quickSearchStore  quickSearchStore
 */

@observer
export class QuickSearch extends Component<QuickSearchProps, null> {
    onOpen = () => {
        const {isOpen, setIsOpen, isDropdownOpen, setIsDropdownOpen} = this.props.quickSearchStore;
        if (!isOpen || !isDropdownOpen) {
            sendMonitor('OpenQuickSearch');
        }
        !isOpen && setIsOpen(true);
        if (!isDropdownOpen) {
            setIsDropdownOpen(true);
            setIsReactPopupOpen(true);
            document.addEventListener('keydown', this.handleKeyDown, true);
        }
    };

    handleKeyDown = e => {
        if (this.props.quickSearchStore.isDropdownOpen) {
            if (e.keyCode === keyCodes.F1) {
                e.preventDefault();
                e.stopPropagation();
                showHelpMessage(texts.help);
            }
        }
    };
    onDropdownClose = () => {
        document.removeEventListener('keydown', this.handleKeyDown, true);
        setIsReactPopupOpen(false);
        returnFocus();
    };

    onClose = () => {
        const {init, setIsDropdownOpen} = this.props.quickSearchStore;
        init();
        setIsDropdownOpen(false);
        this.onDropdownClose();
    };

    onCloseOnlyDropdown = () => {
        const {query, init, setIsDropdownOpen} = this.props.quickSearchStore;
        if (query.length < 3) {
            init();
        }
        setIsDropdownOpen(false);
        this.onDropdownClose();
    };
    handleQueryChange = value => {
        this.props.quickSearchStore.setQuery(value);
    };

    handleAdvancedSearchClick = () => {
        sendMonitor('OpenAdvancedSearchFromQuickSearch');
        renderGeneratorNewState();
        this.onClose();
    };

    handleItemClick = zoomValue => {
        sendMonitor('ClickQuickSearchResult');
        quickSearchEntStart(zoomValue);
    };

    getInputPlaceholder = () => {
        const maxLength = 50;
        const fieldsArr = this.props.quickSearchStore.quickSearchFields;
        const placeholder = texts.placeholder + ' ' + fieldsArr.join(', ');
        const dots = placeholder.length > maxLength ? '...' : '';
        return placeholder.substring(0, maxLength) + dots;
    };

    renderQuickSearchDropdown = () => {
        const {query, results, isSearching, isDropdownOpen, numFound} = this.props.quickSearchStore;
        const placeholder = this.getInputPlaceholder();

        return (
            <SearchDropdown
                circularNavigation
                open={isDropdownOpen}
                closeOnBlur={false}
                closeOnClear={false}
                clearOnClose={false}
                onClose={this.onCloseOnlyDropdown}
                className={styles.dropdownOpen}
                withArrowIcon={false}
                inputProps={{
                    componentClasses: styles.input,
                    prefixIcon: (
                        <Icon
                            iconColor="primary-500"
                            icon={PriorityIcon.searchThin}
                            onClick={this.onClose}
                        />
                    ),
                    size: 'medium',
                    placeholder,
                    onChange: this.handleQueryChange,
                    value: query,
                    onFocus: this.onOpen
                }}
            >
                <Dropdown.Content componentClasses={styles.dropdownConent}>
                    {results.length > 0 && (
                        <List
                            itemProps={{onClick: this.handleItemClick}}
                            autoHeight
                            autoHeightMax={380}
                            renderItem={props => <QuickSearchItem {...props} query={query} />}
                            data={results}
                        />
                    )}
                    {query.length > 0 && (
                        <ResultsStatus
                            isSearching={isSearching}
                            queryLength={query.length}
                            numFound={numFound}
                        />
                    )}
                    {this.renderFooter()}
                </Dropdown.Content>
            </SearchDropdown>
        );
    };

    renderFooter = () => (
        <Flex componentClasses={styles.footer} paddingTop paddingBottom>
            <Button
                skin="text"
                onClick={this.handleAdvancedSearchClick}
                componentClasses={styles.AdvancedSearchButton}
                prefixIcon={PriorityIcon.filter}
            >
                {texts.advancedSearch}
            </Button>
        </Flex>
    );

    render() {
        const {isOpen} = this.props.quickSearchStore;

        return (
            <>
                {isOpen && this.renderQuickSearchDropdown()}
                {!isOpen && (
                    <ButtonWithToolTip
                        tooltip={texts.tooltip}
                        onClick={this.onOpen}
                        skin="secondary"
                        icon={<Icon icon={PriorityIcon.searchThin} iconColor="primary-500" />}
                    />
                )}
            </>
        );
    }
}
